import { requireDashboardOrigin } from "@playbrain/auth/server/dashboardOriginGuard"
import { serverRouteConfig } from "@server/server-route-config"
import { dashboardApiErrorMessage } from "@site-server/dashboard-api-errors"
import { createFileRoute } from "@tanstack/react-router"
import * as z from "zod"

/**
 * Request a sign-in code, then redeem it. One route, two shapes, told apart by
 * whether a code is present.
 */
const requestSchema = z.object({
  email: z.string().email().max(254),
})

/**
 * The address rides with the code now: Supabase only redeems a code against
 * the address it was minted for, so the pair is self-authenticating and the
 * old signed pending-address cookie had nothing left to protect.
 */
const verifySchema = z.object({
  email: z.string().email().max(254),
  code: z.string().min(6).max(10),
})

export const Route = createFileRoute("/api/dashboard/auth/otp")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          const body = await request.json()
          const api = await import("@api/features/dashboard-auth/dashboardOtpApi")

          const verify = verifySchema.safeParse(body)
          if (verify.success) {
            try {
              /** Session cookies are the SDK's; it wrote them onto this response already. */
              const result = await api.verifyDashboardOtp({ input: verify.data, request })
              return Response.json(result.payload)
            } catch (error) {
              return Response.json(
                { error: dashboardApiErrorMessage(error, "Could not sign in.") },
                { status: 401 }
              )
            }
          }

          /**
           * The request step answers 200 whatever happens. It is not an error
           * that an address is unknown -- saying so would turn the login screen
           * into a staff directory.
           */
          try {
            const result = await api.requestDashboardOtp(requestSchema.parse(body))
            return Response.json(result.payload)
          } catch (error) {
            return Response.json(
              { error: dashboardApiErrorMessage(error, "Could not send a code.") },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)
